import { capabilities } from "./content.js";

const renderTags = (items) => `
  <ul class="tag-list" aria-label="相关服务">
    ${items.map((item) => `<li class="tag">${item}</li>`).join("")}
  </ul>
`;

export const initMenu = () => {
  const toggle = document.querySelector("[data-menu-toggle]");
  const menu = document.querySelector("[data-mobile-menu]");
  const links = document.querySelectorAll("[data-mobile-menu-link]");

  if (!toggle || !menu) {
    return;
  }

  const setOpen = (open) => {
    toggle.setAttribute("aria-expanded", String(open));
    toggle.setAttribute("aria-label", open ? "关闭菜单" : "打开菜单");
    menu.setAttribute("aria-hidden", String(!open));
    menu.classList.toggle("is-open", open);
    document.body.classList.toggle("is-menu-open", open);
  };

  toggle.addEventListener("click", () => {
    const open = toggle.getAttribute("aria-expanded") !== "true";
    setOpen(open);
  });

  links.forEach((link) => {
    link.addEventListener("click", () => setOpen(false));
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      setOpen(false);
      toggle.focus();
    }
  });

  window.addEventListener("resize", () => {
    if (window.innerWidth > 1200) {
      setOpen(false);
    }
  });
};

export const initCapabilities = () => {
  const nodes = [...document.querySelectorAll("[data-capability-node]")];
  const panel = document.querySelector("[data-capability-panel]");
  const indexElement = document.querySelector("[data-capability-index]");
  const titleElement = document.querySelector("[data-capability-title]");
  const textElement = document.querySelector("[data-capability-text]");
  const tagsElement = document.querySelector("[data-capability-tags]");

  if (
    nodes.length === 0 ||
    !panel ||
    !indexElement ||
    !titleElement ||
    !textElement ||
    !tagsElement
  ) {
    return;
  }

  const allowHover = window.matchMedia("(hover: hover) and (pointer: fine)");
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  let currentIndex = 0;
  let hoverTimer = 0;
  let panelAnimation = null;

  const activate = (activeIndex) => {
    const capability = capabilities[activeIndex];

    if (!capability || activeIndex === currentIndex) {
      return;
    }

    currentIndex = activeIndex;

    nodes.forEach((node, index) => {
      const active = index === activeIndex;
      node.classList.toggle("is-active", active);
      node.setAttribute("aria-pressed", String(active));
    });

    indexElement.textContent = capability.index;
    titleElement.textContent = capability.title;
    textElement.textContent = capability.description;
    tagsElement.innerHTML = renderTags(capability.tags);

    if (!reducedMotion.matches && typeof panel.animate === "function") {
      panelAnimation?.cancel();
      panelAnimation = panel.animate(
        [
          { opacity: 0.28, transform: "translate3d(0, 0.65rem, 0)" },
          { opacity: 1, transform: "translate3d(0, 0, 0)" },
        ],
        {
          duration: 460,
          easing: "cubic-bezier(0.16, 1, 0.3, 1)",
          fill: "none",
        },
      );
    }
  };

  nodes.forEach((node, index) => {
    node.addEventListener("click", () => {
      window.clearTimeout(hoverTimer);
      activate(index);
    });
    node.addEventListener("focus", () => activate(index));

    if (allowHover.matches) {
      node.addEventListener("pointerenter", () => {
        window.clearTimeout(hoverTimer);
        hoverTimer = window.setTimeout(() => activate(index), 80);
      });
      node.addEventListener("pointerleave", () => {
        window.clearTimeout(hoverTimer);
      });
    }
  });
};

export const initSurfaceLight = () => {
  if (
    window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
    !window.matchMedia("(hover: hover) and (pointer: fine)").matches
  ) {
    return;
  }

  const surfaces = [...document.querySelectorAll("[data-surface-light]")];

  surfaces.forEach((surface) => {
    const position = { x: 50, y: 50, targetX: 50, targetY: 50 };
    let frame = 0;

    const render = () => {
      position.x += (position.targetX - position.x) * 0.16;
      position.y += (position.targetY - position.y) * 0.16;
      surface.style.setProperty("--surface-x", `${position.x.toFixed(2)}%`);
      surface.style.setProperty("--surface-y", `${position.y.toFixed(2)}%`);

      const distance =
        Math.abs(position.targetX - position.x) +
        Math.abs(position.targetY - position.y);

      if (distance > 0.08) {
        frame = window.requestAnimationFrame(render);
        return;
      }

      frame = 0;
    };

    const requestRender = () => {
      if (!frame) {
        frame = window.requestAnimationFrame(render);
      }
    };

    surface.addEventListener("pointerenter", () => {
      requestRender();
    });

    surface.addEventListener("pointermove", (event) => {
      const rect = surface.getBoundingClientRect();
      position.targetX = ((event.clientX - rect.left) / rect.width) * 100;
      position.targetY = ((event.clientY - rect.top) / rect.height) * 100;
      requestRender();
    });

    surface.addEventListener("pointerleave", () => {
      position.targetX = 50;
      position.targetY = 50;
      requestRender();
    });
  });
};

export const initCaseSlider = () => {
  const viewport = document.querySelector("[data-case-viewport]");
  const track = document.querySelector("[data-case-track]");
  const previousButton = document.querySelector("[data-case-prev]");
  const nextButton = document.querySelector("[data-case-next]");
  const cards = [...document.querySelectorAll("[data-case-card]")];
  const section = viewport?.closest("[data-cases-scene]");
  const nativeCarousel = window.matchMedia("(max-width: 74.99rem)");

  if (!viewport || !track || cards.length === 0) {
    return;
  }

  let activeIndex = 0;

  const clampIndex = (index) => {
    return Math.min(Math.max(index, 0), cards.length - 1);
  };

  const cardDistance = () => {
    const firstCard = cards[0];
    const secondCard = cards[1];

    if (!secondCard) {
      return firstCard.getBoundingClientRect().width;
    }

    return secondCard.offsetLeft - firstCard.offsetLeft;
  };

  const nativeOffset = () => {
    const card = cards[activeIndex];

    return Math.max(
      card.offsetLeft - (viewport.clientWidth - card.offsetWidth) / 2,
      0,
    );
  };

  const render = ({ navigate = false, progressOverride = null } = {}) => {
    const progress =
      progressOverride ??
      (cards.length > 1 ? activeIndex / (cards.length - 1) : 0);
    const offset = Math.min(activeIndex * cardDistance(), track.scrollWidth);

    cards.forEach((card, index) => {
      card.classList.toggle("is-active", index === activeIndex);
    });

    if (!section?.__caseScrollScene && !nativeCarousel.matches) {
      track.style.transform = `translate3d(${-offset}px, 0, 0)`;
    }

    if (navigate && nativeCarousel.matches) {
      viewport.scrollTo({
        left: nativeOffset(),
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
      });
    }

    viewport.style.setProperty("--case-progress", progress.toFixed(4));

    if (navigate && section?.__caseScrollScene) {
      const scene = section.__caseScrollScene;
      const sceneProgress = section.__caseScrollProgressMap
        ? section.__caseScrollProgressMap(progress)
        : progress;
      const target = scene.start + sceneProgress * (scene.end - scene.start);

      window.scrollTo({
        top: target,
        behavior: "auto",
      });
    }

    if (previousButton) {
      previousButton.disabled = activeIndex === 0;
      previousButton.setAttribute("aria-disabled", String(activeIndex === 0));
    }

    if (nextButton) {
      nextButton.disabled = activeIndex === cards.length - 1;
      nextButton.setAttribute(
        "aria-disabled",
        String(activeIndex === cards.length - 1),
      );
    }
  };

  const move = (direction) => {
    activeIndex = clampIndex(activeIndex + direction);
    render({ navigate: true });
  };

  previousButton?.addEventListener("click", () => move(-1));
  nextButton?.addEventListener("click", () => move(1));

  let dragStartX = 0;
  let isDragging = false;

  viewport.addEventListener("pointerdown", (event) => {
    if (nativeCarousel.matches) {
      return;
    }

    isDragging = true;
    dragStartX = event.clientX;
    viewport.setPointerCapture?.(event.pointerId);
  });

  viewport.addEventListener("pointerup", (event) => {
    if (nativeCarousel.matches || !isDragging) {
      return;
    }

    const distance = event.clientX - dragStartX;
    isDragging = false;

    if (Math.abs(distance) > 50) {
      move(distance < 0 ? 1 : -1);
    }
  });

  viewport.addEventListener("pointercancel", () => {
    isDragging = false;
  });

  section?.addEventListener("caseprogress", (event) => {
    const progress = Math.min(Math.max(event.detail?.progress ?? 0, 0), 1);
    activeIndex = clampIndex(Math.round(progress * (cards.length - 1)));
    render({ progressOverride: progress });
  });

  let nativeScrollFrame = 0;
  const syncNativeScroll = () => {
    nativeScrollFrame = 0;

    if (!nativeCarousel.matches) {
      return;
    }

    const viewportCenter = viewport.scrollLeft + viewport.clientWidth / 2;
    let nearestIndex = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;

    cards.forEach((card, index) => {
      const cardCenter = card.offsetLeft + card.offsetWidth / 2;
      const distance = Math.abs(cardCenter - viewportCenter);

      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });

    activeIndex = nearestIndex;
    const firstCenter = cards[0].offsetLeft + cards[0].offsetWidth / 2;
    const lastCard = cards[cards.length - 1];
    const lastCenter = lastCard.offsetLeft + lastCard.offsetWidth / 2;
    const travel = Math.max(lastCenter - firstCenter, 1);
    const progress = Math.min(
      Math.max((viewportCenter - firstCenter) / travel, 0),
      1,
    );

    render({
      progressOverride: progress,
    });
  };

  viewport.addEventListener(
    "scroll",
    () => {
      if (!nativeScrollFrame) {
        nativeScrollFrame = window.requestAnimationFrame(syncNativeScroll);
      }
    },
    { passive: true },
  );

  nativeCarousel.addEventListener("change", () => {
    track.style.removeProperty("transform");
    render({ navigate: nativeCarousel.matches });
  });

  window.addEventListener(
    "resize",
    () => render({ navigate: nativeCarousel.matches }),
    { passive: true },
  );
  render();
};

export const initShowreel = () => {
  const button = document.querySelector("[data-showreel-button]");
  const stage = button?.closest(".showreel__stage");
  const video = stage?.querySelector("[data-showreel-video]");
  const timeElement = document.querySelector("[data-showreel-time]");
  const labelElement = document.querySelector("[data-showreel-label]");
  const detailElement = document.querySelector("[data-showreel-detail]");
  const activeTitleElement = document.querySelector(
    "[data-showreel-active-title]",
  );
  const clipButtons = [...document.querySelectorAll("[data-reel-clip]")];
  const timeline = stage?.querySelector("[data-video-timeline]");
  const timelineFill = stage?.querySelector("[data-video-timeline-fill]");

  if (!button || !stage || !video) {
    return;
  }

  let activeTitle = clipButtons[0]?.dataset.reelTitle ?? "动态母带";
  let scrubbing = false;

  const syncTimeline = () => {
    if (!timeline) {
      return;
    }

    const duration = Number.isFinite(video.duration) ? video.duration : 0;
    const progress = duration > 0 ? video.currentTime / duration : 0;
    const percentage = Math.min(Math.max(progress * 100, 0), 100);

    timeline.style.setProperty("--video-progress", `${percentage}%`);
    timeline.setAttribute("aria-valuenow", String(Math.round(percentage)));
    timeline.setAttribute(
      "aria-valuetext",
      `${Math.round(percentage)}%，${formatTime(video.currentTime)} / ${formatTime(duration)}`,
    );
    timelineFill?.setAttribute("data-progress", String(Math.round(percentage)));
  };

  const seekToPercentage = (percentage) => {
    if (!Number.isFinite(video.duration) || video.duration <= 0) {
      return;
    }

    const normalized = Math.min(Math.max(percentage, 0), 1);
    video.currentTime = normalized * video.duration;
    syncTimeline();
  };

  const seekFromPointer = (event) => {
    if (!timeline) {
      return;
    }

    const rect = timeline.getBoundingClientRect();
    seekToPercentage((event.clientX - rect.left) / Math.max(rect.width, 1));
  };

  const setPlaying = (playing) => {
    stage.classList.toggle("is-playing", playing);
    button.setAttribute("aria-pressed", String(playing));
    button.setAttribute(
      "aria-label",
      playing ? `暂停${activeTitle}` : `播放${activeTitle}`,
    );
  };

  const selectClip = async (clipButton) => {
    const source = clipButton.dataset.reelSrc;

    if (!source) {
      return;
    }

    activeTitle = clipButton.dataset.reelTitle ?? "动态母带";
    const poster = clipButton.dataset.reelPoster ?? "";
    const index = clipButton.dataset.reelIndex ?? "REEL";
    const note = clipButton.dataset.reelNote ?? "";
    const duration = clipButton.dataset.reelDuration ?? "00:00";
    const alreadyActive = clipButton.classList.contains("is-active");

    clipButtons.forEach((item) => {
      const active = item === clipButton;
      item.classList.toggle("is-active", active);
      item.setAttribute("aria-pressed", String(active));
    });

    if (labelElement) {
      labelElement.textContent = `${index} · ${activeTitle}`;
    }

    if (detailElement) {
      detailElement.textContent = note;
    }

    if (activeTitleElement) {
      activeTitleElement.textContent = activeTitle;
    }

    if (timeElement) {
      timeElement.textContent = `00:00 / ${duration}`;
    }

    if (timeline) {
      timeline.style.setProperty("--video-progress", "0%");
      timeline.setAttribute("aria-valuenow", "0");
    }

    button.setAttribute("aria-label", `播放${activeTitle}`);

    if (alreadyActive) {
      if (video.paused) {
        try {
          await video.play();
        } catch (error) {
          console.warn("Showreel playback could not start", error);
        }
      }
      return;
    }

    video.pause();
    setPlaying(false);
    stage.classList.add("is-switching");
    video.poster = poster;
    video.src = source;
    video.load();

    window.setTimeout(() => stage.classList.remove("is-switching"), 420);

    try {
      await video.play();
    } catch (error) {
      console.warn("Selected showreel clip is ready for manual playback", error);
    }
  };

  clipButtons.forEach((clipButton) => {
    clipButton.addEventListener("click", () => selectClip(clipButton));
  });

  button.addEventListener("click", async () => {
    if (video.paused) {
      try {
        await video.play();
        setPlaying(true);
      } catch (error) {
        console.warn("Showreel playback could not start", error);
        setPlaying(false);
      }
      return;
    }

    video.pause();
    setPlaying(false);
  });

  video.addEventListener("play", () => setPlaying(true));
  video.addEventListener("pause", () => setPlaying(false));

  const formatTime = (seconds) => {
    const safeSeconds = Number.isFinite(seconds) ? Math.max(seconds, 0) : 0;
    const minutes = Math.floor(safeSeconds / 60);
    const remainder = Math.floor(safeSeconds % 60);

    return `${String(minutes).padStart(2, "0")}:${String(remainder).padStart(2, "0")}`;
  };

  const updateTime = () => {
    if (timeElement) {
      timeElement.textContent = `${formatTime(video.currentTime)} / ${formatTime(video.duration || 15)}`;
    }

    syncTimeline();
  };

  timeline?.addEventListener("pointerdown", (event) => {
    scrubbing = true;
    timeline.setPointerCapture?.(event.pointerId);
    seekFromPointer(event);
  });

  timeline?.addEventListener("pointermove", (event) => {
    if (scrubbing) {
      seekFromPointer(event);
    }
  });

  timeline?.addEventListener("pointerup", (event) => {
    if (!scrubbing) {
      return;
    }

    seekFromPointer(event);
    scrubbing = false;
    timeline.releasePointerCapture?.(event.pointerId);
  });

  timeline?.addEventListener("pointercancel", () => {
    scrubbing = false;
  });

  timeline?.addEventListener("keydown", (event) => {
    if (!Number.isFinite(video.duration) || video.duration <= 0) {
      return;
    }

    if (event.key === "Home" || event.key === "End") {
      event.preventDefault();
      seekToPercentage(event.key === "Home" ? 0 : 1);
      return;
    }

    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault();
      const direction = event.key === "ArrowRight" ? 1 : -1;
      video.currentTime = Math.min(
        Math.max(video.currentTime + direction * 5, 0),
        video.duration,
      );
      syncTimeline();
    }
  });

  video.addEventListener("loadedmetadata", updateTime);
  video.addEventListener("timeupdate", updateTime);
  video.addEventListener("durationchange", updateTime);
  updateTime();
};

export const initLightStudy = () => {
  const stage = document.querySelector("[data-light-study]");
  const frames = [...document.querySelectorAll("[data-light-frame]")];

  if (!stage || frames.length === 0) {
    return;
  }

  let activeIndex = Math.max(
    frames.findIndex((frame) => frame.classList.contains("is-selected")),
    0,
  );

  const activate = (index, { focus = false } = {}) => {
    activeIndex = (index + frames.length) % frames.length;
    stage.classList.add("has-selection");
    stage.style.setProperty(
      "--light-selection",
      String(activeIndex / Math.max(frames.length - 1, 1)),
    );

    frames.forEach((frame, frameIndex) => {
      const selected = frameIndex === activeIndex;
      frame.classList.toggle("is-selected", selected);
      frame.setAttribute("aria-pressed", String(selected));
    });

    if (focus) {
      frames[activeIndex].focus({ preventScroll: true });
    }
  };

  frames.forEach((frame, index) => {
    frame.addEventListener("click", () => activate(index));
    frame.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        activate(index);
      }

      if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        event.preventDefault();
        activate(index + (event.key === "ArrowRight" ? 1 : -1), {
          focus: true,
        });
      }
    });
  });

  activate(activeIndex);
};

export const initArchiveInspector = () => {
  const items = [...document.querySelectorAll("[data-archive-shot]")];
  const dialog = document.querySelector("[data-media-inspector]");
  const image = dialog?.querySelector("[data-inspector-image]");
  const title = dialog?.querySelector("[data-inspector-title]");
  const note = dialog?.querySelector("[data-inspector-note]");
  const indexLabel = dialog?.querySelector("[data-inspector-index]");
  const currentLabel = dialog?.querySelector("[data-inspector-current]");
  const totalLabel = dialog?.querySelector("[data-inspector-total]");
  const previousButton = dialog?.querySelector("[data-inspector-prev]");
  const nextButton = dialog?.querySelector("[data-inspector-next]");
  const closeButtons = [...(dialog?.querySelectorAll("[data-inspector-close]") ?? [])];

  if (!dialog || !image || items.length === 0) {
    return;
  }

  let activeIndex = 0;
  let lastTrigger = null;
  let switchTimer = 0;

  const render = (index, direction = 1) => {
    activeIndex = (index + items.length) % items.length;
    const item = items[activeIndex];

    window.clearTimeout(switchTimer);
    dialog.classList.remove("is-switching-forward", "is-switching-backward");
    void dialog.offsetWidth;
    dialog.classList.add(
      direction < 0 ? "is-switching-backward" : "is-switching-forward",
    );

    image.src = item.dataset.archiveSrc ?? "";
    image.alt = item.dataset.archiveAlt ?? "";
    if (title) title.textContent = item.dataset.archiveTitle ?? "影像档案";
    if (note) note.textContent = item.dataset.archiveNote ?? "真实世界观察";
    if (indexLabel) {
      indexLabel.textContent = `FRAME / ${String(activeIndex + 1).padStart(2, "0")}`;
    }
    if (currentLabel) {
      currentLabel.textContent = String(activeIndex + 1).padStart(2, "0");
    }
    if (totalLabel) {
      totalLabel.textContent = String(items.length).padStart(2, "0");
    }

    switchTimer = window.setTimeout(() => {
      dialog.classList.remove(
        "is-switching-forward",
        "is-switching-backward",
      );
    }, 620);
  };

  const open = (index, trigger) => {
    lastTrigger = trigger;
    render(index);
    document.body.classList.add("is-inspector-open");

    if (typeof dialog.showModal === "function") {
      dialog.showModal();
    } else {
      dialog.setAttribute("open", "");
    }

    dialog.querySelector(".media-inspector__close")?.focus();
  };

  const close = () => {
    if (typeof dialog.close === "function") {
      dialog.close();
    } else {
      dialog.removeAttribute("open");
      document.body.classList.remove("is-inspector-open");
      lastTrigger?.focus({ preventScroll: true });
    }
  };

  items.forEach((item, index) => {
    item.addEventListener("click", () => open(index, item));
    item.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        open(index, item);
      }
    });
  });

  previousButton?.addEventListener("click", () => render(activeIndex - 1, -1));
  nextButton?.addEventListener("click", () => render(activeIndex + 1, 1));
  closeButtons.forEach((button) => button.addEventListener("click", close));

  dialog.addEventListener("cancel", (event) => {
    event.preventDefault();
    close();
  });

  dialog.addEventListener("close", () => {
    document.body.classList.remove("is-inspector-open");
    lastTrigger?.focus({ preventScroll: true });
  });

  dialog.addEventListener("keydown", (event) => {
    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault();
      const direction = event.key === "ArrowRight" ? 1 : -1;
      render(activeIndex + direction, direction);
    }
  });
};

export const initDepthSurfaces = () => {
  if (
    window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
    !window.matchMedia("(hover: hover) and (pointer: fine)").matches
  ) {
    return;
  }

  const surfaces = [...document.querySelectorAll("[data-depth-surface]")];

  surfaces.forEach((surface) => {
    const position = { x: 0, y: 0, targetX: 0, targetY: 0 };
    let frame = 0;

    const render = () => {
      position.x += (position.targetX - position.x) * 0.12;
      position.y += (position.targetY - position.y) * 0.12;
      surface.style.setProperty("--depth-x", `${position.x.toFixed(2)}px`);
      surface.style.setProperty("--depth-y", `${position.y.toFixed(2)}px`);
      surface.style.setProperty(
        "--depth-light-x",
        `${(50 + position.x * 2.2).toFixed(1)}%`,
      );
      surface.style.setProperty(
        "--depth-light-y",
        `${(50 + position.y * 2.2).toFixed(1)}%`,
      );

      if (
        Math.abs(position.targetX - position.x) +
          Math.abs(position.targetY - position.y) >
        0.05
      ) {
        frame = window.requestAnimationFrame(render);
      } else {
        frame = 0;
      }
    };

    const requestRender = () => {
      if (!frame) frame = window.requestAnimationFrame(render);
    };

    surface.addEventListener("pointermove", (event) => {
      const rect = surface.getBoundingClientRect();
      const x = (event.clientX - rect.left) / Math.max(rect.width, 1) - 0.5;
      const y = (event.clientY - rect.top) / Math.max(rect.height, 1) - 0.5;
      position.targetX = x * -12;
      position.targetY = y * -10;
      requestRender();
    });

    surface.addEventListener("pointerleave", () => {
      position.targetX = 0;
      position.targetY = 0;
      requestRender();
    });
  });
};

export const initPressRipples = () => {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    return;
  }

  const selectors = [
    "a.button",
    ".circle-button",
    ".reel-clip",
    ".orbit-node",
    ".contact-channel",
    ".media-inspector button",
  ].join(",");

  document.querySelectorAll(selectors).forEach((control) => {
    control.addEventListener("pointerdown", (event) => {
      const rect = control.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height) * 1.7;
      const ripple = document.createElement("span");
      ripple.className = "interaction-ripple";
      ripple.style.width = `${size}px`;
      ripple.style.height = `${size}px`;
      ripple.style.left = `${event.clientX - rect.left - size / 2}px`;
      ripple.style.top = `${event.clientY - rect.top - size / 2}px`;
      ripple.addEventListener("animationend", () => ripple.remove(), {
        once: true,
      });
      control.append(ripple);
    });
  });
};

export const initYear = () => {
  const yearElements = document.querySelectorAll("[data-year]");
  const currentYear = String(new Date().getFullYear());

  yearElements.forEach((element) => {
    element.textContent = currentYear;
  });
};

export const initPageTransitions = () => {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    return;
  }

  document.addEventListener("click", (event) => {
    const link = event.target.closest("a");

    if (
      !link ||
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      link.target === "_blank" ||
      link.hasAttribute("download")
    ) {
      return;
    }

    const href = link.getAttribute("href") ?? "";

    if (
      href.startsWith("#") ||
      href.startsWith("mailto:") ||
      href.startsWith("tel:") ||
      href.startsWith("javascript:")
    ) {
      return;
    }

    const destination = new URL(link.href, window.location.href);

    if (destination.origin !== window.location.origin) {
      return;
    }

    event.preventDefault();
    document.body.classList.add("is-page-leaving");

    window.setTimeout(() => {
      window.location.assign(destination.href);
    }, 430);
  });
};

export const initInteractions = () => {
  initMenu();
  initCapabilities();
  initSurfaceLight();
  initCaseSlider();
  initShowreel();
  initLightStudy();
  initArchiveInspector();
  initDepthSurfaces();
  initPressRipples();
  initYear();
  initPageTransitions();
};
